/**
 * RequireAdmin (dual-mode via accountsEnabled — see AdminAuthContext)
 *
 * Route wrapper for /admin/*. Shows the right sign-in UI for the current mode:
 *   - password mode  → an admin password form.
 *   - keycloak mode  → a "Sign in with Trusscore" SSO button, plus a discreet
 *                      "Admin recovery" toggle that reveals a password field for
 *                      the break-glass super-admin login.
 * Once an admin session exists it renders the child admin UI.
 *
 * Sibling export: RequireSuperAdmin — guard for super-only sub-routes. Renders an
 * inline access-denied notice for plain admin sessions instead of redirecting.
 */

import React, { useState } from 'react';
import { Lock, LogIn, Loader2, ShieldAlert, KeyRound } from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';

interface Props {
  children: React.ReactNode;
}

export const RequireSuperAdmin: React.FC<Props> = ({ children }) => {
  const { isAdminAuthenticated, isSuperAdmin, isChecking } = useAdminAuth();

  if (isChecking) return null;

  // Should never hit this branch in practice — RequireAdmin wraps the whole
  // /admin tree and the sign-in prompt runs first. Kept as a safety net.
  if (!isAdminAuthenticated) return null;

  if (!isSuperAdmin) {
    return (
      <div className="bg-white rounded-3 shadow-sm p-4">
        <div className="d-flex align-items-center gap-2 mb-2">
          <ShieldAlert size={20} className="text-warning" />
          <h4 className="fw-bold m-0">Super admin access required</h4>
        </div>
        <p className="text-muted small mb-0">
          This section is restricted. Sign in with an account (or password) that has
          the super-admin tier to view settings.
        </p>
      </div>
    );
  }

  return <>{children}</>;
};

/** Shared password entry used by both the password mode and the break-glass recovery. */
const AdminPasswordForm: React.FC<{
  label: string;
  autoFocus?: boolean;
  onSubmit: (password: string) => Promise<boolean>;
  wrongMsg: string;
}> = ({ label, autoFocus, onSubmit, wrongMsg }) => {
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!password) {
      setError('Please enter the password.');
      return;
    }
    setSubmitting(true);
    try {
      const ok = await onSubmit(password);
      if (!ok) {
        setError(wrongMsg);
        setPassword('');
      }
    } catch {
      setError('Unable to reach the server. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      {error && <div className="alert alert-danger py-2 small mb-3">{error}</div>}
      <div className="mb-3 text-start">
        <label htmlFor="admin-password" className="form-label fw-semibold small">
          {label}
        </label>
        <input
          id="admin-password"
          type="password"
          className="form-control"
          value={password}
          onChange={e => setPassword(e.target.value)}
          autoFocus={autoFocus}
          autoComplete="current-password"
          disabled={submitting}
        />
      </div>
      <button
        type="submit"
        className="btn btn-primary w-100 d-inline-flex align-items-center justify-content-center gap-2"
        disabled={submitting}
      >
        {submitting ? (
          <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Checking…</>
        ) : (
          <><LogIn size={16} /> Sign in</>
        )}
      </button>
    </form>
  );
};

export const RequireAdmin: React.FC<Props> = ({ children }) => {
  const { isAdminAuthenticated, isChecking, mode, loginWithPassword, loginKeycloak } = useAdminAuth();
  const [recoveryOpen, setRecoveryOpen] = useState(false);

  if (isChecking) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
        <Loader2 size={24} className="text-muted" style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  if (isAdminAuthenticated) return <>{children}</>;

  return (
    <div className="d-flex align-items-center justify-content-center py-5">
      <div className="card shadow-sm" style={{ maxWidth: 420, width: '100%' }}>
        <div className="card-body p-4 text-center">
          <div
            className="d-inline-flex align-items-center justify-content-center mb-2"
            style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(13,110,253,0.08)' }}
          >
            <Lock size={22} className="text-primary" />
          </div>
          <h4 className="fw-bold mb-1">Admin Access</h4>

          {mode === 'password' ? (
            <>
              <p className="text-muted small mb-3">Enter the admin password to continue.</p>
              <AdminPasswordForm
                label="Password"
                autoFocus
                onSubmit={loginWithPassword}
                wrongMsg="Incorrect password."
              />
            </>
          ) : (
            <>
              <p className="text-muted small mb-3">
                Sign in with a Trusscore account that has admin access.
              </p>
              <button
                type="button"
                className="btn btn-primary w-100 d-inline-flex align-items-center justify-content-center gap-2"
                onClick={() => loginKeycloak()}
              >
                <LogIn size={16} /> Sign in with Trusscore
              </button>

              <div className="mt-3">
                <button
                  type="button"
                  className="btn btn-link btn-sm text-muted d-inline-flex align-items-center gap-1"
                  onClick={() => setRecoveryOpen(v => !v)}
                >
                  <KeyRound size={13} /> Admin recovery
                </button>
              </div>

              {recoveryOpen && (
                <div className="mt-2 pt-3 border-top">
                  <p className="text-muted small mb-3">
                    Break-glass super-admin login. Requires the recovery password set in
                    <code> config.php</code>.
                  </p>
                  <AdminPasswordForm
                    label="Recovery password"
                    autoFocus
                    onSubmit={loginWithPassword}
                    wrongMsg="Incorrect or disabled recovery password."
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequireAdmin;
